import React, { useEffect, useMemo, useRef, useState } from "react";
import katex from "katex";
import "katex/dist/katex.min.css";
import { ApiService } from "../../services/api";
import { useModal } from "../../hooks/useModal";
import Modal from "../../components/ui/Modal";
import type { MaarifExportTarget } from "../../types/maarif-question-wizard";
import type { CurriculumUnitHealth } from "../../types/curriculum";

const TURKISH_CHAR_RE = /[çğıöşüÇĞİÖŞÜ]/;
const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const renderKatex = (src: string, display = false): string => {
  try {
    return katex.renderToString(src, {
      throwOnError: false,
      displayMode: display,
      output: "html",
      strict: false,
    });
  } catch {
    return src;
  }
};

// LaTeX sembol komutları → Unicode karşılığı (fallback render için)
const LATEX_SYMBOL_MAP: Record<string, string> = {
  "\\\\bullet": "•", "\\\\cdot": "·", "\\\\times": "×", "\\\\div": "÷",
  "\\\\wedge": "∧", "\\\\vee": "∨", "\\\\neg": "¬", "\\\\lnot": "¬",
  "\\\\forall": "∀", "\\\\exists": "∃", "\\\\in": "∈", "\\\\notin": "∉",
  "\\\\ge": "≥", "\\\\geq": "≥", "\\\\le": "≤", "\\\\leq": "≤",
  "\\\\ne": "≠", "\\\\neq": "≠", "\\\\approx": "≈", "\\\\equiv": "≡",
  "\\\\Rightarrow": "⇒", "\\\\Leftarrow": "⇐", "\\\\Leftrightarrow": "⇔",
  "\\\\to": "→", "\\\\rightarrow": "→", "\\\\leftarrow": "←",
  "\\\\infty": "∞", "\\\\pm": "±", "\\\\mp": "∓",
  "\\\\alpha": "α", "\\\\beta": "β", "\\\\gamma": "γ", "\\\\delta": "δ",
  "\\\\theta": "θ", "\\\\lambda": "λ", "\\\\mu": "μ", "\\\\pi": "π",
  "\\\\sigma": "σ", "\\\\phi": "φ", "\\\\omega": "ω",
  "\\\\Delta": "Δ", "\\\\Omega": "Ω",
  "\\\\square": "□", "\\\\blacksquare": "■", "\\\\star": "★",
  "\\\\triangle": "△", "\\\\blacktriangle": "▲",
};

// Türkçe karışmış math segmentini KaTeX kullanmadan plain HTML olarak render et
const renderMixedTurkishMath = (src: string): string => {
  let out = src;
  // \text{...} ve \mathrm{...} → içeriği aynen al
  out = out.replace(/\\(?:text|mathrm|textrm|mbox)\{([^{}]*)\}/g, "$1");
  // Sembol komutlarını Unicode'a çevir
  for (const [pattern, sym] of Object.entries(LATEX_SYMBOL_MAP)) {
    out = out.replace(new RegExp(pattern, "g"), sym);
  }
  // Üs/alt yazımı basit dönüştür ($x^2$ → x²) — sadece tek karakter
  const SUP_MAP: Record<string, string> = { "0": "⁰", "1": "¹", "2": "²", "3": "³", "4": "⁴", "5": "⁵", "6": "⁶", "7": "⁷", "8": "⁸", "9": "⁹", "+": "⁺", "-": "⁻", "n": "ⁿ" };
  const SUB_MAP: Record<string, string> = { "0": "₀", "1": "₁", "2": "₂", "3": "₃", "4": "₄", "5": "₅", "6": "₆", "7": "₇", "8": "₈", "9": "₉" };
  out = out.replace(/\^([0-9+\-n])/g, (_, c) => SUP_MAP[c] || `^${c}`);
  out = out.replace(/_([0-9])/g, (_, c) => SUB_MAP[c] || `_${c}`);
  // Kalan ters bölü grupları (\frac vs.) bırak — bu fallback olduğu için ideal değil ama okunur
  return `<span class="font-normal not-italic">${escapeHtml(out)}</span>`;
};

// Bir math segmentinin içinde Türkçe karakter varsa KaTeX bypass et;
// hiç yoksa normal KaTeX render uygula.
// Sebep: KaTeX_Main font Türkçe glyph'leri tanımıyor → "bölünmesi" → "b o ¨ l u ¨ nmesi"
const renderMathSegment = (src: string, display: boolean): string => {
  if (TURKISH_CHAR_RE.test(src)) {
    return renderMixedTurkishMath(src);
  }
  return renderKatex(src, display);
};

function MathText({ value }: { value: string }) {
  if (!value) return null;
  // $$...$$ ve $...$ parça parça ayır
  const parts: Array<{ type: "text" | "inline" | "display"; content: string }> = [];
  let i = 0;
  let buf = "";
  while (i < value.length) {
    if (value[i] === "$") {
      const isDisplay = value[i + 1] === "$";
      const delim = isDisplay ? "$$" : "$";
      const start = i + delim.length;
      const end = value.indexOf(delim, start);
      if (end === -1) {
        buf += value[i];
        i += 1;
        continue;
      }
      if (buf) { parts.push({ type: "text", content: buf }); buf = ""; }
      parts.push({ type: isDisplay ? "display" : "inline", content: value.slice(start, end) });
      i = end + delim.length;
    } else {
      buf += value[i];
      i += 1;
    }
  }
  if (buf) parts.push({ type: "text", content: buf });

  return (
    <>
      {parts.map((p, idx) =>
        p.type === "text" ? (
          <React.Fragment key={idx}>{p.content}</React.Fragment>
        ) : (
          <span
            key={idx}
            className={p.type === "display" ? "block my-2" : "inline-block align-middle"}
            dangerouslySetInnerHTML={{ __html: renderMathSegment(p.content, p.type === "display") }}
          />
        )
      )}
    </>
  );
}

type Course = { code: string; name: string };
type UnitSummary = {
  unitCode: string;
  unitName: string;
  unitOrder: number;
  lessonHours: number;
  outcomeCount: number;
  description: string;
};
type ProcessComponent = { letter: string; statement: string };
type Outcome = { code: string; statement: string; processComponents: ProcessComponent[] };

const labelClass = "block text-sm font-medium text-slate-700 mb-1";
const fieldClass =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-50";
const sectionClass =
  "rounded-xl border border-slate-200 bg-white p-5 shadow-sm";
const tagClass =
  "inline-flex items-center rounded-full bg-blue-50 text-blue-700 text-xs font-medium px-2 py-1 mr-1 mb-1 border border-blue-100";

// Sayfadan ayrılıp dönünce (örn. Dizgici'ye gidip geri) kaldığın yerden devam etmek için
// çalışma durumu sessionStorage'da tutulur.
const CURRICULUM_TEST_STORAGE_KEY = "curriculumTest:v1";

export default function CurriculumTestPage() {
  const { showAlert, showToast } = useModal();
  // Geri yükleme sırasında cascade efektlerinin (ders→sınıf→ünite) alt seçimleri sıfırlamasını engeller
  const hydratingRef = useRef(false);

  const [courses, setCourses] = useState<Course[]>([]);
  const [courseCode, setCourseCode] = useState("");

  const [grades, setGrades] = useState<number[]>([]);
  const [grade, setGrade] = useState<number | "">("");

  const [units, setUnits] = useState<UnitSummary[]>([]);
  const [unitCode, setUnitCode] = useState("");

  const [unit, setUnit] = useState<any>(null);
  const [outcomeCode, setOutcomeCode] = useState("");
  const [processLetter, setProcessLetter] = useState("");

  const [difficulty, setDifficulty] = useState<"Kolay" | "Orta" | "Zor">("Orta");
  const [lengthPreset, setLengthPreset] = useState<"Kısa" | "Orta" | "Uzun">("Orta");
  const [wantsVisual, setWantsVisual] = useState(false);
  const [withJudge, setWithJudge] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");

  const [enriched, setEnriched] = useState<any>(null);
  const [context, setContext] = useState("");
  const [rationale, setRationale] = useState("");
  const [question, setQuestion] = useState<any>(null);
  // Bağlam grubu (paylaşılan pasaj + N soru)
  const [groupMode, setGroupMode] = useState(false);
  const [groupCount, setGroupCount] = useState(3);
  const [group, setGroup] = useState<any>(null);
  const [groupExporting, setGroupExporting] = useState(false);
  const [groupExportResult, setGroupExportResult] = useState<{ questionBankId: string; questionCount: number } | null>(null);
  const [judgeReport, setJudgeReport] = useState<any>(null);
  const [judgeAttempts, setJudgeAttempts] = useState<any[]>([]);
  const [judgeExhausted, setJudgeExhausted] = useState(false);

  const [busy, setBusy] = useState<"" | "context" | "question" | "group">("");
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState<{
    questionBankId: string;
    questionBankTitle: string | null;
    questionCount: number;
    appendedCount: number;
    exportedAt: string;
  } | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // Seed sağlık kontrolü
  const [health, setHealth] = useState<CurriculumUnitHealth | null>(null);
  const [healthExpanded, setHealthExpanded] = useState(false);

  // Sticky hedef test (session boyunca aynı teste eklenir)
  const [targetTestId, setTargetTestId] = useState<string | null>(null);
  const [targetTestName, setTargetTestName] = useState<string | null>(null);

  // Hedef seçim modali
  const [targetModalOpen, setTargetModalOpen] = useState(false);
  const [availableTargets, setAvailableTargets] = useState<MaarifExportTarget[]>([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [selectedExistingTargetId, setSelectedExistingTargetId] = useState<string>("");

  // Üretilen soru değişince export durumunu sıfırla
  useEffect(() => {
    setExportResult(null);
    setExportError(null);
  }, [question]);

  useEffect(() => {
    ApiService.getCurriculumCourses().then(setCourses).catch((e) => {
      showAlert({ title: "Hata", message: "Dersler yüklenemedi: " + e.message, type: "error" });
    });
  }, [showAlert]);

  useEffect(() => {
    if (!courseCode) { setGrades([]); if (!hydratingRef.current) setGrade(""); return; }
    ApiService.getCurriculumGrades(courseCode).then((g) => { setGrades(g); if (!hydratingRef.current) setGrade(""); }).catch(() => setGrades([]));
  }, [courseCode]);

  useEffect(() => {
    if (!courseCode || !grade) { setUnits([]); if (!hydratingRef.current) setUnitCode(""); return; }
    ApiService.getCurriculumUnits(courseCode, Number(grade)).then((u) => { setUnits(u); if (!hydratingRef.current) setUnitCode(""); }).catch(() => setUnits([]));
  }, [courseCode, grade]);

  useEffect(() => {
    if (!unitCode) { setUnit(null); if (!hydratingRef.current) setOutcomeCode(""); setHealth(null); return; }
    ApiService.getCurriculumUnit(unitCode).then((u) => { setUnit(u); if (!hydratingRef.current) { setOutcomeCode(""); setProcessLetter(""); setEnriched(null); } }).catch(() => setUnit(null));
    ApiService.getCurriculumUnitHealth(unitCode).then(setHealth).catch(() => setHealth(null));
  }, [unitCode]);

  // Mount: kaldığı yerden devam — sessionStorage'dan geri yükle (Dizgici'ye gidip dönünce sıfırlanmasın)
  useEffect(() => {
    let snap: any = null;
    try { const raw = sessionStorage.getItem(CURRICULUM_TEST_STORAGE_KEY); if (raw) snap = JSON.parse(raw); } catch { /* yoksay */ }
    if (!snap) return;
    hydratingRef.current = true;
    // cascade dışı (doğrudan) durum
    if (snap.difficulty) setDifficulty(snap.difficulty);
    if (snap.lengthPreset) setLengthPreset(snap.lengthPreset);
    if (typeof snap.wantsVisual === "boolean") setWantsVisual(snap.wantsVisual);
    if (typeof snap.withJudge === "boolean") setWithJudge(snap.withJudge);
    if (snap.customPrompt) setCustomPrompt(snap.customPrompt);
    if (snap.context) setContext(snap.context);
    if (snap.rationale) setRationale(snap.rationale);
    if (snap.question) setQuestion(snap.question);
    if (snap.judgeReport) setJudgeReport(snap.judgeReport);
    if (Array.isArray(snap.judgeAttempts)) setJudgeAttempts(snap.judgeAttempts);
    if (typeof snap.judgeExhausted === "boolean") setJudgeExhausted(snap.judgeExhausted);
    if (snap.exportResult) setExportResult(snap.exportResult);
    if (snap.targetTestId) setTargetTestId(snap.targetTestId);
    if (snap.targetTestName) setTargetTestName(snap.targetTestName);
    if (typeof snap.groupMode === "boolean") setGroupMode(snap.groupMode);
    if (snap.groupCount) setGroupCount(snap.groupCount);
    if (snap.group) setGroup(snap.group);
    if (snap.groupExportResult) setGroupExportResult(snap.groupExportResult);
    // cascade seçimleri — efektler veriyi çeker, hydratingRef sıfırlamayı engeller
    if (snap.courseCode) setCourseCode(snap.courseCode);
    if (snap.grade !== undefined && snap.grade !== null && snap.grade !== "") setGrade(snap.grade);
    if (snap.unitCode) setUnitCode(snap.unitCode);
    if (snap.outcomeCode) setOutcomeCode(snap.outcomeCode);
    if (snap.processLetter) setProcessLetter(snap.processLetter);
    const t = setTimeout(() => { hydratingRef.current = false; }, 2500);
    return () => clearTimeout(t);
  }, []);

  // Çalışma durumunu sakla (her değişimde) — geri yükleme sırasında yazma
  useEffect(() => {
    if (hydratingRef.current) return;
    try {
      sessionStorage.setItem(CURRICULUM_TEST_STORAGE_KEY, JSON.stringify({
        courseCode, grade, unitCode, outcomeCode, processLetter,
        difficulty, lengthPreset, wantsVisual, withJudge, customPrompt,
        context, rationale, question, judgeReport, judgeAttempts, judgeExhausted,
        targetTestId, targetTestName, exportResult,
        groupMode, groupCount, group, groupExportResult,
      }));
    } catch { /* kota vb. yoksay */ }
  }, [courseCode, grade, unitCode, outcomeCode, processLetter, difficulty, lengthPreset,
      wantsVisual, withJudge, customPrompt, context, rationale, question, judgeReport,
      judgeAttempts, judgeExhausted, targetTestId, targetTestName, exportResult,
      groupMode, groupCount, group, groupExportResult]);

  const selectedOutcome: Outcome | undefined = useMemo(
    () => unit?.outcomes?.find((o: Outcome) => o.code === outcomeCode),
    [unit, outcomeCode]
  );

  useEffect(() => {
    if (!outcomeCode) { setEnriched(null); return; }
    ApiService.getCurriculumEnrichedOutcome(outcomeCode, processLetter || null).then(setEnriched).catch(() => setEnriched(null));
  }, [outcomeCode, processLetter]);

  const onGenerateContext = async () => {
    if (!outcomeCode) {
      await showAlert({ title: "Eksik", message: "Önce bir öğrenme çıktısı seçin.", type: "warning" });
      return;
    }
    setBusy("context");
    setQuestion(null);
    if (groupMode) { setGroup(null); setGroupExportResult(null); }
    try {
      // Grup modunda bağlam kazanım seviyesinde (process=null) ve çok yönlü olmalı (N soruyu desteklesin)
      const groupHint = `Bu bağlam, aynı kazanımın ${groupCount} farklı sorusuna ORTAK kaynak olacak. Tek bir senaryo/metin olsun; birden çok süreç bileşenini ölçen soruları destekleyecek kadar zengin ve veri açısından dolu olsun.`;
      const res = await ApiService.generateMaarifContextV2({
        outcomeCode,
        processLetter: groupMode ? null : (processLetter || null),
        difficulty,
        lengthPreset: groupMode ? "Uzun" : lengthPreset,
        customPrompt: [customPrompt || null, groupMode ? groupHint : null].filter(Boolean).join("\n\n") || null,
      });
      setContext(res.context);
      setRationale(res.rationale);
    } catch (e: any) {
      await showAlert({ title: "Hata", message: e.message || "Bilinmeyen hata", type: "error" });
    } finally {
      setBusy("");
    }
  };

  const onGenerateQuestion = async () => {
    if (!context) {
      await showAlert({ title: "Eksik", message: "Önce bağlam üretin.", type: "warning" });
      return;
    }
    setBusy("question");
    setJudgeReport(null);
    setJudgeAttempts([]);
    setJudgeExhausted(false);
    try {
      const res = await ApiService.generateMaarifQuestionV2({
        outcomeCode,
        processLetter: processLetter || null,
        difficulty,
        lengthPreset,
        wantsVisual,
        withJudge,
        customPrompt: customPrompt || null,
        context,
      });
      setQuestion(res.question);
      setJudgeReport(res.judgeReport || null);
      setJudgeAttempts(res.judgeAttempts || []);
      setJudgeExhausted(!!res.judgeExhausted);
    } catch (e: any) {
      await showAlert({ title: "Hata", message: e.message || "Bilinmeyen hata", type: "error" });
    } finally {
      setBusy("");
    }
  };

  // Bağlam grubu 2. aşama: ÜRETİLEN pasajdan N soru (AYNI zorluk, farklı süreç bileşeni)
  const onGenerateGroupQuestions = async () => {
    if (!outcomeCode) {
      await showAlert({ title: "Eksik", message: "Önce bir öğrenme çıktısı seçin.", type: "warning" });
      return;
    }
    if (!context) {
      await showAlert({ title: "Eksik", message: "Önce bağlam üretin.", type: "warning" });
      return;
    }
    setBusy("group");
    setGroup(null);
    setGroupExportResult(null);
    try {
      const res = await ApiService.generateMaarifQuestionsFromContextV2({
        outcomeCode,
        context,
        count: groupCount,
        difficulty,
        customPrompt: customPrompt || null,
        wantsVisual,
        withJudge,
      });
      setGroup(res);
    } catch (e: any) {
      await showAlert({ title: "Hata", message: e.message || "Sorular üretilemedi", type: "error" });
    } finally {
      setBusy("");
    }
  };

  // Grubu Dizgici'ye aktar: pasaj ilk sorunun intro_text'ine (showIntroText), sorular ardışık
  const onExportGroup = async (questionBankId: string | null) => {
    if (!group?.questions?.length) return;
    setGroupExporting(true);
    try {
      const passage = `${group.context}\n\nAşağıdaki soruları yukarıdaki metne göre cevaplayınız.`;
      const multiQuestions = group.questions.map((gq: any, i: number) => ({
        ...gq.question,
        ...(i === 0 ? { introText: passage } : {}),
      }));
      const title = `${group.unitName || group.unitCode} — ${group.outcomeCode} (Bağlam Grubu)`;
      const res = await ApiService.exportMaarifQuestionToQuestionBank({
        title,
        lessonTitle: `${group.course?.name || ""} ${group.grade ? group.grade + ". Sınıf" : ""}`.trim(),
        content: { multiQuestions },
        ...(questionBankId ? { questionBankId } : {}),
      } as any);
      setGroupExportResult({ questionBankId: res.questionBankId, questionCount: res.questionCount ?? multiQuestions.length });
      if (res.questionBankId) { setTargetTestId(res.questionBankId); setTargetTestName(res.questionBankTitle || null); }
      showToast("Grup Dizgici'ye aktarıldı", "success");
    } catch (e: any) {
      await showAlert({ title: "Aktarım Hatası", message: e?.message || "Grup aktarılamadı", type: "error" });
    } finally {
      setGroupExporting(false);
    }
  };

  const performExport = async (questionBankId: string | null) => {
    if (!question || !enriched) return;
    setExporting(true);
    setExportError(null);

    const today = new Date().toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
    const processLabel = processLetter ? `.${processLetter}` : "";
    const title = `${enriched.unit?.name || "Maarif"} — ${outcomeCode}${processLabel} (${today})`;
    const lessonTitle = `${enriched.course?.name || "Matematik"} ${enriched.grade}. Sınıf`;

    try {
      const result = await ApiService.exportMaarifQuestionToQuestionBank({
        title,
        lessonTitle,
        questionBankId: questionBankId || null,
        content: {
          question,
          multiQuestions: null,
          rewriteQuestion: null,
          detectedMetadata: {
            lessonTitle: enriched.course?.name || null,
            gradeLabel: `${enriched.grade}. Sınıf`,
            gainText: enriched.outcome?.statement || null,
            processComponentCode: question.measuredProcessComponentCode || null,
            processComponentLabel: question.measuredProcessComponentLabel || null,
          },
        },
      });
      setExportResult({
        questionBankId: result.questionBankId,
        questionBankTitle: result.questionBankTitle,
        questionCount: result.questionCount,
        appendedCount: result.appendedCount,
        exportedAt: result.exportedAt,
      });
      setTargetTestId(result.questionBankId);
      setTargetTestName(result.questionBankTitle);
    } catch (e: any) {
      const message = e?.message || "Aktarım başarısız";
      setExportError(message);
      await showAlert({ title: "Aktarım Hatası", message, type: "error" });
    } finally {
      setExporting(false);
    }
  };

  // Hedef seçme modalı hem tekli soru hem grup için ortak; hangisi olduğu exportMode ile belli
  const [exportMode, setExportMode] = useState<"single" | "group">("single");

  const openTargetModal = async () => {
    setSelectedExistingTargetId("");
    setTargetModalOpen(true);
    setLoadingTargets(true);
    try {
      const list = await ApiService.listMaarifExportTargets(10);
      setAvailableTargets(list.rows || []);
    } catch {
      setAvailableTargets([]);
    } finally {
      setLoadingTargets(false);
    }
  };

  const onExportToQuestionBank = async () => {
    if (!question || !enriched) return;
    setExportMode("single");
    // Sticky hedef varsa modal açma, doğrudan ekle
    if (targetTestId) {
      await performExport(targetTestId);
      return;
    }
    await openTargetModal();
  };

  const onExportGroupToQuestionBank = async () => {
    if (!group?.questions?.length) return;
    setExportMode("group");
    if (targetTestId) {
      await onExportGroup(targetTestId);
      return;
    }
    await openTargetModal();
  };

  const onChooseNewTest = async () => {
    setTargetModalOpen(false);
    if (exportMode === "group") await onExportGroup(null);
    else await performExport(null);
  };

  const onChooseExistingTest = async () => {
    if (!selectedExistingTargetId) {
      await showAlert({ title: "Eksik", message: "Bir test seçin veya yeni test oluşturun.", type: "warning" });
      return;
    }
    setTargetModalOpen(false);
    if (exportMode === "group") await onExportGroup(selectedExistingTargetId);
    else await performExport(selectedExistingTargetId);
  };

  const onResetTarget = () => {
    setTargetTestId(null);
    setTargetTestName(null);
    setExportResult(null);
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const onDownloadPdf = async () => {
    if (!question || !enriched) return;
    setDownloadingPdf(true);
    try {
      const { blob, fileName } = await ApiService.downloadMaarifQuestionV2Pdf({
        question,
        context,
        enriched,
        meta: {
          outcomeCode: outcomeCode || null,
          processLetter: processLetter || null,
        },
        judgeReport: judgeReport || null,
        judgeAttempts: judgeAttempts.length ? judgeAttempts : null,
        judgeExhausted: judgeExhausted || false,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || `maarif-soru-${outcomeCode}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      await showAlert({ title: "PDF Hatası", message: e?.message || "PDF üretilemedi", type: "error" });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const [groupDownloadingPdf, setGroupDownloadingPdf] = useState(false);
  const onDownloadGroupPdf = async () => {
    if (!group?.questions?.length) return;
    setGroupDownloadingPdf(true);
    try {
      const { blob, fileName } = await ApiService.downloadMaarifQuestionGroupV2Pdf({
        questions: group.questions,
        context: group.context,
        enriched: group.curriculumSnapshot || enriched,
        meta: { outcomeCode: group.outcomeCode || outcomeCode || null, unitName: group.unitName || null },
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || `maarif-grup-${group.outcomeCode || outcomeCode}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      await showAlert({ title: "PDF Hatası", message: e?.message || "PDF üretilemedi", type: "error" });
    } finally {
      setGroupDownloadingPdf(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-6">
      <div className="mx-auto w-full max-w-7xl">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Maarif Wizard — Müfredat Tabanlı Prototip (V2)</h1>
            <p className="mt-1 text-sm text-slate-600">
              MEB Türkiye Yüzyılı Maarif Modeli resmi müfredatı üzerinden bağlam ve soru üretimi.
              Mevcut wizard'a alternatif test sayfası.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              hydratingRef.current = false;
              try { sessionStorage.removeItem(CURRICULUM_TEST_STORAGE_KEY); } catch { /* yoksay */ }
              setCourseCode(""); setGrade(""); setUnitCode("");
              setOutcomeCode(""); setProcessLetter("");
              setContext(""); setRationale(""); setQuestion(null);
              setJudgeReport(null); setJudgeAttempts([]); setJudgeExhausted(false);
              setExportResult(null); setTargetTestId(null); setTargetTestName(null);
              setGroup(null); setGroupExportResult(null);
            }}
            className="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100"
            title="Tüm seçim ve üretimleri temizle"
          >
            Sıfırla
          </button>
        </header>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Left: Drill-down */}
          <div className="lg:col-span-1 space-y-4">
            <div className={sectionClass}>
              <h2 className="text-sm font-semibold text-slate-900 mb-3">1) Müfredat Seçimi</h2>

              <div className="space-y-3">
                <div>
                  <label className={labelClass}>Ders</label>
                  <select className={fieldClass} value={courseCode} onChange={(e) => setCourseCode(e.target.value)}>
                    <option value="">Seçiniz...</option>
                    {courses.map((c) => (<option key={c.code} value={c.code}>{c.name}</option>))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Sınıf</label>
                  <select className={fieldClass} value={grade} onChange={(e) => setGrade(e.target.value ? Number(e.target.value) : "")} disabled={!courseCode}>
                    <option value="">Seçiniz...</option>
                    {grades.map((g) => (<option key={g} value={g}>{g}. Sınıf</option>))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Ünite</label>
                  <select className={fieldClass} value={unitCode} onChange={(e) => setUnitCode(e.target.value)} disabled={!grade}>
                    <option value="">Seçiniz...</option>
                    {units.map((u) => (<option key={u.unitCode} value={u.unitCode}>{u.unitOrder}. {u.unitName}</option>))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Öğrenme Çıktısı</label>
                  <select className={fieldClass} value={outcomeCode} onChange={(e) => setOutcomeCode(e.target.value)} disabled={!unit}>
                    <option value="">Seçiniz...</option>
                    {unit?.outcomes?.map((o: Outcome) => (
                      <option key={o.code} value={o.code}>{o.code} — {o.statement.slice(0, 55)}{o.statement.length > 55 ? "…" : ""}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Süreç Bileşeni (opsiyonel — boş = tüm bileşenler)</label>
                  <select className={fieldClass} value={processLetter} onChange={(e) => setProcessLetter(e.target.value)} disabled={!selectedOutcome}>
                    <option value="">Tümü</option>
                    {selectedOutcome?.processComponents?.map((pc) => (
                      <option key={pc.letter} value={pc.letter}>({pc.letter}) {pc.statement.slice(0, 60)}{pc.statement.length > 60 ? "…" : ""}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {health && (
              <div className={`${sectionClass} border-l-4 ${
                health.status === "ok" ? "border-l-emerald-500" :
                health.status === "warning" ? "border-l-amber-500" :
                "border-l-rose-500"
              }`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg" aria-hidden>
                      {health.status === "ok" ? "🟢" : health.status === "warning" ? "🟡" : "🔴"}
                    </span>
                    <h2 className="text-sm font-semibold text-slate-900">Seed Sağlığı</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => setHealthExpanded(!healthExpanded)}
                    className="text-xs text-slate-600 hover:text-slate-900 underline"
                  >
                    {healthExpanded ? "Kapat" : "Detay"}
                  </button>
                </div>

                <div className="text-xs text-slate-700">
                  {health.status === "ok" && (
                    <span><strong>Hazır</strong> — {health.summary.outcomesOk}/{health.summary.outcomeCount} outcome temiz, batch üretime uygun.</span>
                  )}
                  {health.status === "warning" && (
                    <span><strong>Uyarı</strong> — {health.summary.warningFailed} ikincil eksik var ama soru üretimi yapılabilir.</span>
                  )}
                  {health.status === "critical" && (
                    <span><strong>Eksik veri</strong> — {health.summary.criticalFailed} kritik kontrol başarısız ({health.summary.outcomesCritical} outcome). Batch üretimi kilitli.</span>
                  )}
                </div>

                {!health.canBatchGenerate && (
                  <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                    Tekli soru üretimi yapılabilir, ancak toplu üretim için seed tamamlanmalı.
                  </div>
                )}

                {healthExpanded && (
                  <div className="mt-3 space-y-3 text-xs">
                    <div>
                      <div className="font-semibold text-slate-700 mb-1">Ünite Kontrolleri</div>
                      <ul className="space-y-1">
                        {health.unitChecks.map((c) => (
                          <li key={c.id} className="flex items-center gap-1.5">
                            <span aria-hidden>{c.passed ? "✓" : c.level === "critical" ? "✗" : "⚠"}</span>
                            <span className={c.passed ? "text-emerald-700" : c.level === "critical" ? "text-rose-700" : "text-amber-700"}>
                              {c.label}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <div className="font-semibold text-slate-700 mb-1">Outcome Durumları</div>
                      <ul className="space-y-1.5">
                        {health.outcomes.map((o) => {
                          const failed = o.checks.filter((c) => !c.passed);
                          return (
                            <li key={o.code} className="border border-slate-200 rounded p-2 bg-slate-50">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-slate-800">
                                  <span aria-hidden className="mr-1">
                                    {o.status === "ok" ? "🟢" : o.status === "warning" ? "🟡" : "🔴"}
                                  </span>
                                  {o.code}
                                </span>
                                <span className="text-slate-500 text-[10px]">{o.processComponentCount} süreç bileşeni</span>
                              </div>
                              {failed.length > 0 && (
                                <ul className="mt-1 space-y-0.5 text-[11px]">
                                  {failed.map((c) => (
                                    <li key={c.id} className={c.level === "critical" ? "text-rose-700" : c.level === "warning" ? "text-amber-700" : "text-slate-500"}>
                                      {c.level === "critical" ? "✗" : "⚠"} {c.label}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className={sectionClass}>
              <h2 className="text-sm font-semibold text-slate-900 mb-3">2) Üretim Ayarları</h2>
              <div className="space-y-3">
                <div>
                  <label className={labelClass}>Zorluk (MEB Farklılaştırma)</label>
                  <select className={fieldClass} value={difficulty} onChange={(e) => setDifficulty(e.target.value as any)}>
                    <option value="Kolay">Kolay (Destekleme)</option>
                    <option value="Orta">Orta (Standart)</option>
                    <option value="Zor">Zor (Zenginleştirme)</option>
                  </select>
                  <p className="mt-1 text-xs text-slate-500">
                    MEB Türkiye Yüzyılı Maarif Modeli zorluk taksonomisi. Üniteye özel destekleme/zenginleştirme tanımları prompt'a otomatik eklenir.
                  </p>
                </div>
                <div>
                  <label className={labelClass}>Uzunluk</label>
                  <select className={fieldClass} value={lengthPreset} onChange={(e) => setLengthPreset(e.target.value as any)}>
                    <option>Kısa</option><option>Orta</option><option>Uzun</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={wantsVisual} onChange={(e) => setWantsVisual(e.target.checked)} />
                    Görsel istiyorum
                  </label>
                </div>
                <div>
                  <label className="flex items-start gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={withJudge}
                      onChange={(e) => setWithJudge(e.target.checked)}
                      className="mt-0.5"
                    />
                    <span>
                      Judge ile denetle <span className="text-xs text-slate-500"></span>
                    </span>
                  </label>
                </div>
                <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-2.5">
                  <label className="flex items-start gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={groupMode} onChange={(e) => setGroupMode(e.target.checked)} className="mt-0.5" />
                    <span>
                      Bağlam grubu (çoklu soru) <span className="text-xs text-slate-500">— 1 kazanım + ortak pasaj + N soru (farklı süreç bileşeni, artan zorluk)</span>
                    </span>
                  </label>
                  {groupMode && (
                    <div className="mt-2 flex items-center gap-2 pl-6">
                      <span className="text-xs text-slate-600">Soru sayısı:</span>
                      <select className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm" value={groupCount} onChange={(e) => setGroupCount(Number(e.target.value))}>
                        <option value={2}>2</option><option value={3}>3</option><option value={4}>4</option>
                      </select>
                    </div>
                  )}
                </div>
                <div>
                  <label className={labelClass}>Ek talimat (opsiyonel)</label>
                  <textarea className={fieldClass} rows={3} value={customPrompt} onChange={(e) => setCustomPrompt(e.target.value)} placeholder="Örn: Bilim insanı örneği Cezeri olsun…" />
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                {groupMode ? (
                  <>
                    <button
                      className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                      onClick={onGenerateContext}
                      disabled={!outcomeCode || busy !== ""}>
                      {busy === "context" ? "Bağlam üretiliyor..." : "1) Bağlam Üret"}
                    </button>
                    <button
                      className="flex-1 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
                      onClick={onGenerateGroupQuestions}
                      disabled={!context || busy !== ""}>
                      {busy === "group" ? `Üretiliyor (${groupCount} soru)...` : `2) Bu bağlamdan ${groupCount} soru üret`}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                      onClick={onGenerateContext}
                      disabled={!outcomeCode || busy !== ""}>
                      {busy === "context" ? "Bağlam üretiliyor..." : "Bağlam Üret"}
                    </button>
                    <button
                      className="flex-1 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                      onClick={onGenerateQuestion}
                      disabled={!context || busy !== ""}>
                      {busy === "question" ? "Soru üretiliyor..." : "Soru Üret"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Right: Enriched context preview + output */}
          <div className="lg:col-span-2 space-y-4">
            {enriched && (
              <div className={sectionClass}>
                <h2 className="text-sm font-semibold text-slate-900 mb-3">Müfredat Bağlamı</h2>
                <div className="text-xs text-slate-600 space-y-2">
                  <div><span className="font-medium">Ünite:</span> {enriched.unit.name} ({enriched.unit.code}) — {enriched.unit.lessonHours} ders saati</div>
                  <div><span className="font-medium">Çıktı:</span> {enriched.outcome.code} — {enriched.outcome.statement}</div>
                  {enriched.focusedProcessComponent && (
                    <div><span className="font-medium">Odak:</span> ({enriched.focusedProcessComponent.letter}) {enriched.focusedProcessComponent.statement}</div>
                  )}
                  <div><span className="font-medium">Ön bilgi:</span> {enriched.priorKnowledge || "—"}</div>
                  <div><span className="font-medium">Köprü:</span> {enriched.bridging || "—"}</div>
                  <div className="flex flex-wrap">
                    <span className="font-medium mr-1">Anahtar kavramlar:</span>
                    {(enriched.keyConcepts || []).map((k: string) => <span key={k} className={tagClass}>{k}</span>)}
                  </div>
                  <div className="flex flex-wrap">
                    <span className="font-medium mr-1">İçerik çerçevesi:</span>
                    {(enriched.contentFramework || []).map((k: string) => <span key={k} className={tagClass}>{k}</span>)}
                  </div>
                  <div className="flex flex-wrap">
                    <span className="font-medium mr-1">Beceriler:</span>
                    {[
                      ...(enriched.skills?.field || []),
                      ...(enriched.skills?.conceptual || []),
                      ...(enriched.skills?.tendency || []),
                    ].map((s: any) => <span key={s.code} className={tagClass}>{s.code} {s.label}</span>)}
                  </div>
                  <div className="flex flex-wrap">
                    <span className="font-medium mr-1">Disiplinler arası:</span>
                    {(enriched.interdisciplinary || []).map((k: string) => <span key={k} className={tagClass}>{k}</span>)}
                  </div>
                  {enriched.misconceptions?.length > 0 && (
                    <div className="flex flex-wrap">
                      <span className="font-medium mr-1 text-amber-700">Kavram yanılgıları (çeldirici):</span>
                      {enriched.misconceptions.map((k: string) => <span key={k} className={`${tagClass} bg-amber-50 text-amber-700 border-amber-200`}>{k}</span>)}
                    </div>
                  )}
                </div>
              </div>
            )}

            {(context || rationale) && (
              <div className={sectionClass}>
                <h2 className="text-sm font-semibold text-slate-900 mb-3">Üretilen Bağlam</h2>
                {context && <p className="text-sm leading-relaxed text-slate-800 whitespace-pre-line"><MathText value={context} /></p>}
                {rationale && (
                  <p className="mt-3 text-xs text-slate-500 italic"><span className="font-medium">Gerekçe:</span> <MathText value={rationale} /></p>
                )}
              </div>
            )}

            {judgeReport && (
              <div className={`${sectionClass} border-l-4 ${
                judgeReport.verdict === "pass" ? "border-l-emerald-500" :
                judgeExhausted ? "border-l-rose-500" : "border-l-amber-500"
              }`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg" aria-hidden>
                      {judgeReport.verdict === "pass" ? "🟢" : judgeExhausted ? "🔴" : "🟡"}
                    </span>
                    <h2 className="text-sm font-semibold text-slate-900">
                      Judge Denetimi
                      <span className="ml-2 text-xs font-normal text-slate-500">
                        {judgeAttempts.length}/{3} deneme
                      </span>
                    </h2>
                  </div>
                </div>

                <div className="text-xs text-slate-700 mb-2">
                  {judgeReport.verdict === "pass" && (
                    <span><strong>Kabul edildi</strong> — 4 eksen de geçti{judgeAttempts.length > 1 ? `, ${judgeAttempts.length}. denemede` : ""}.</span>
                  )}
                  {judgeReport.verdict === "fail" && judgeExhausted && (
                    <span className="text-rose-700"><strong>Manuel inceleme</strong> — 3 denemede de geçemedi. Soru yine de gösteriliyor, gözle kontrol et.</span>
                  )}
                  {judgeReport.judgeError && (
                    <span className="text-amber-700">Denetim çağrısı başarısız oldu (modelden cevap alınamadı). Soru gösteriliyor ama denetlenmedi.</span>
                  )}
                </div>

                {judgeReport.summary && (
                  <p className="text-xs text-slate-600 italic mb-2">{judgeReport.summary}</p>
                )}

                {judgeReport.axes && Object.keys(judgeReport.axes).length > 0 && (
                  <ul className="space-y-1 text-xs mb-2">
                    {[
                      { key: "processMatch", label: "Süreç uyumu" },
                      { key: "realism", label: "Gerçekçilik" },
                      { key: "numericalConsistency", label: "Sayısal tutarlılık" },
                      { key: "solutionCorrectness", label: "Çözüm doğruluğu" },
                    ].map((a) => {
                      const ax = judgeReport.axes?.[a.key];
                      if (!ax) return null;
                      return (
                        <li key={a.key} className="flex items-start gap-1.5">
                          <span aria-hidden>{ax.passed ? "✓" : "✗"}</span>
                          <span className={ax.passed ? "text-emerald-700" : "text-rose-700"}>
                            <strong>{a.label}:</strong> {ax.reason || (ax.passed ? "geçti" : "kaldı")}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {judgeAttempts.length > 1 && (
                  <details className="text-xs text-slate-600 mt-2">
                    <summary className="cursor-pointer font-medium">Önceki deneme özetleri ({judgeAttempts.length})</summary>
                    <ul className="mt-1 space-y-1">
                      {judgeAttempts.map((a: any) => (
                        <li key={a.attempt}>
                          <span className="font-mono">#{a.attempt}</span> — {a.verdict === "pass" ? "✓ pass" : `✗ fail (${(a.failedAxes || []).join(", ")})`} — {a.summary}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}

            {group && (
              <div className={sectionClass}>
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-slate-900">Bağlam Grubu — {group.count} soru</h2>
                  <span className="text-xs text-slate-500">{group.outcomeCode}</span>
                </div>
                {/* Ortak pasaj */}
                <div className="mb-4 rounded-lg border border-violet-200 bg-violet-50/60 p-3">
                  <div className="mb-1 text-xs font-semibold text-violet-700">Ortak Metin (Pasaj)</div>
                  <p className="whitespace-pre-line text-sm text-slate-700"><MathText value={group.context} /></p>
                  <p className="mt-2 text-xs italic text-slate-500">Aşağıdaki soruları yukarıdaki metne göre cevaplayınız.</p>
                </div>
                {/* Sorular */}
                <div className="space-y-4">
                  {group.questions.map((gq: any, idx: number) => (
                    <div key={idx} className="rounded-lg border border-slate-100 p-3">
                      <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
                        <span className="rounded bg-slate-100 px-1.5 py-0.5">Soru {gq.order}</span>
                        {gq.processLetter && <span className="rounded bg-slate-100 px-1.5 py-0.5">süreç {gq.processLetter}</span>}
                        <span className="rounded bg-slate-100 px-1.5 py-0.5">{gq.difficulty}</span>
                        {gq.judgeReport?.verdict && <span className={gq.judgeReport.verdict === "pass" ? "text-emerald-500" : "text-amber-500"}>judge: {gq.judgeReport.verdict}</span>}
                      </div>
                      <p className="text-sm font-medium text-slate-900"><MathText value={gq.question?.questionStem} /></p>
                      {gq.question?.image?.base64 && (
                        <div className="mt-2">
                          <img
                            src={`data:${gq.question.image.mimeType || "image/png"};base64,${gq.question.image.base64}`}
                            alt={gq.question.image.title || "Soru görseli"}
                            className="max-h-64 rounded-md border border-slate-200"
                          />
                          {gq.question.image.caption && (
                            <p className="mt-1 text-xs text-slate-500 italic">{gq.question.image.caption}</p>
                          )}
                        </div>
                      )}
                      <div className="mt-1.5 space-y-1">
                        {(["A", "B", "C", "D", "E"] as const).map((k) => gq.question?.choices?.[k] ? (
                          <div key={k} className={`flex gap-2 rounded-md p-1.5 text-sm ${gq.question.correctAnswer === k ? "bg-emerald-50 border border-emerald-200" : "border border-slate-100"}`}>
                            <span className="w-5 font-semibold text-slate-700">{k})</span>
                            <span className="flex-1 text-slate-800"><MathText value={gq.question.choices[k]} /></span>
                            {gq.question.correctAnswer === k && <span className="text-xs text-emerald-700">✓</span>}
                          </div>
                        ) : null)}
                      </div>
                      <details className="mt-2 text-xs text-slate-600">
                        <summary className="cursor-pointer font-medium">Çözüm &amp; çeldirici analizi</summary>
                        <div className="mt-2 space-y-1">
                          {gq.question?.shortJustification && (
                            <div><span className="font-medium">Kısa gerekçe:</span> <MathText value={gq.question.shortJustification} /></div>
                          )}
                          {(["A", "B", "C", "D", "E"] as const).map((k) => gq.question?.distractorAnalysis?.[k] ? (
                            <div key={k}><span className="font-medium">{k}:</span> <MathText value={gq.question.distractorAnalysis[k]} /></div>
                          ) : null)}
                          {gq.judgeReport?.verdict && (
                            <div className="text-violet-700"><span className="font-medium">Judge:</span> {gq.judgeReport.verdict}{gq.judgeReport.summary ? ` — ${gq.judgeReport.summary}` : ""}</div>
                          )}
                        </div>
                      </details>
                    </div>
                  ))}
                </div>
                {/* Dizgici aktarım + PDF */}
                <div className="mt-4 border-t border-slate-200 pt-4">
                  {targetTestId && (
                    <div className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                      <div className="truncate"><span className="font-semibold">Hedef test:</span> <span className="font-medium">{targetTestName || targetTestId}</span></div>
                      <button onClick={onResetTarget} className="shrink-0 text-blue-600 hover:underline">değiştir</button>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-3">
                    <button onClick={onExportGroupToQuestionBank} disabled={groupExporting}
                      className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50">
                      {groupExporting ? "Aktarılıyor..." : (targetTestId ? "📋 Bu teste ekle" : "📋 Grubu Dizgici'ye Aktar")}
                    </button>
                    <button onClick={onDownloadGroupPdf} disabled={groupDownloadingPdf}
                      className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                      {groupDownloadingPdf ? "PDF hazırlanıyor..." : "⬇ PDF İndir"}
                    </button>
                    {groupExportResult && (
                      <a href={`/question-bank-indesign/${groupExportResult.questionBankId}/edit`} target="_blank" rel="noreferrer"
                        className="text-sm font-medium text-violet-700 hover:underline">
                        Dizgici'de Aç → ({groupExportResult.questionCount} soru)
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}

            {question && (
              <div className={sectionClass}>
                <h2 className="text-sm font-semibold text-slate-900 mb-3">Üretilen Soru</h2>
                <div className="space-y-3 text-sm">
                  {question.context && (
                    <p className="text-slate-700 whitespace-pre-line border-l-4 border-slate-200 pl-3">
                      <MathText value={question.context} />
                    </p>
                  )}
                  {question.image?.base64 && (
                    <div className="my-3">
                      <img
                        src={`data:${question.image.mimeType || "image/png"};base64,${question.image.base64}`}
                        alt={question.image.title || "Soru görseli"}
                        className="max-w-md max-h-96 rounded-lg border border-slate-200 shadow-sm"
                      />
                      {question.image.caption && (
                        <p className="mt-1 text-xs text-slate-500 italic">{question.image.caption}</p>
                      )}
                    </div>
                  )}
                  <p className="font-medium text-slate-900"><MathText value={question.questionStem} /></p>
                  <div className="space-y-1">
                    {(["A", "B", "C", "D", "E"] as const).map((k) => (
                      <div key={k} className={`flex gap-2 p-2 rounded-md ${question.correctAnswer === k ? "bg-emerald-50 border border-emerald-200" : "border border-slate-100"}`}>
                        <span className="font-semibold w-6 text-slate-700">{k})</span>
                        <span className="flex-1 text-slate-800"><MathText value={question.choices[k]} /></span>
                        {question.correctAnswer === k && <span className="text-emerald-700 text-xs font-medium">✓ Doğru</span>}
                      </div>
                    ))}
                  </div>
                  <details className="text-xs text-slate-600">
                    <summary className="cursor-pointer font-medium">Çözüm & çeldirici analizi</summary>
                    <div className="mt-2 space-y-2">
                      <div><span className="font-medium">Kısa gerekçe:</span> <MathText value={question.shortJustification} /></div>
                      {(["A", "B", "C", "D", "E"] as const).map((k) => (
                        <div key={k}><span className="font-medium">{k}:</span> <MathText value={question.distractorAnalysis?.[k] || ""} /></div>
                      ))}
                    </div>
                  </details>

                  {/* Dizgici aktarım bölümü */}
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    {targetTestId && (
                      <div className="mb-3 flex items-center justify-between gap-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800">
                        <div className="truncate">
                          <span className="font-semibold">Hedef test:</span>{" "}
                          <span className="font-medium">{targetTestName || targetTestId}</span>
                          {exportResult?.questionCount ? (
                            <span className="text-blue-600"> · {exportResult.questionCount} soru</span>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          onClick={onResetTarget}
                          className="text-blue-700 hover:text-blue-900 underline whitespace-nowrap"
                        >
                          Yeni test başlat
                        </button>
                      </div>
                    )}
                    {exportResult ? (
                      <div className="flex items-center justify-between gap-3 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3">
                        <div className="text-sm text-emerald-800">
                          <span className="font-semibold">
                            ✓ {exportResult.questionCount > exportResult.appendedCount ? "Mevcut teste eklendi" : "Yeni test oluşturuldu"}.
                          </span>{" "}
                          <span className="text-xs text-emerald-700">
                            Toplam {exportResult.questionCount} soru
                          </span>
                        </div>
                        <a
                          href={`/question-bank-indesign/${exportResult.questionBankId}/edit`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 whitespace-nowrap"
                        >
                          Dizgici'de Aç →
                        </a>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={onExportToQuestionBank}
                          disabled={exporting}
                          className="flex-1 rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {exporting ? (
                            <>
                              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"></span>
                              Aktarılıyor...
                            </>
                          ) : targetTestId ? (
                            <>📋 Bu Teste Soru Ekle</>
                          ) : (
                            <>📋 Soru Bankasına (Dizgici) Aktar</>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={onDownloadPdf}
                          disabled={downloadingPdf}
                          className="rounded-lg bg-slate-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                        >
                          {downloadingPdf ? (
                            <>
                              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"></span>
                              PDF...
                            </>
                          ) : (
                            <>⬇ PDF İndir</>
                          )}
                        </button>
                      </div>
                    )}
                    {exportError && (
                      <p className="mt-2 text-xs text-rose-700">{exportError}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={targetModalOpen}
        onClose={() => setTargetModalOpen(false)}
        title="Soruyu nereye aktaralım?"
        size="sm"
      >
        <p className="text-xs text-slate-600 -mt-1 mb-4">
          Bu seans boyunca aynı teste eklenir. İstediğinde üstteki "Yeni test başlat" ile sıfırlayabilirsin.
        </p>

        <div className="space-y-3">
          <button
            type="button"
            onClick={onChooseNewTest}
            className="w-full rounded-lg border border-purple-200 bg-purple-50 px-4 py-3 text-left hover:bg-purple-100"
          >
            <div className="text-sm font-semibold text-purple-900">+ Yeni test oluştur</div>
            <div className="text-xs text-purple-700 mt-0.5">Dizgici'de yeni bir test açılır, soru ilk soru olur.</div>
          </button>

          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-sm font-semibold text-slate-800 mb-2">Mevcut bir teste ekle</div>
            {loadingTargets ? (
              <div className="text-xs text-slate-500">Yükleniyor...</div>
            ) : availableTargets.length === 0 ? (
              <div className="text-xs text-slate-500">Henüz maarif testin yok — yeni test oluştur.</div>
            ) : (
              <>
                <select
                  className={fieldClass}
                  value={selectedExistingTargetId}
                  onChange={(e) => setSelectedExistingTargetId(e.target.value)}
                >
                  <option value="">Test seçin...</option>
                  {availableTargets.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.testAdi} · {t.questionCount} soru
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={onChooseExistingTest}
                  disabled={!selectedExistingTargetId}
                  className="mt-2 w-full rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Bu teste ekle
                </button>
              </>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
